/**
 * dd-support-hours.js
 *
 * Single clock, many consumers. Any element carrying
 * `[data-dd-support-hours]` gets a live read-out of Drop Dead's
 * support status computed against the `America/Denver` IANA zone
 * (so "MST" holds up across DST automatically) and the
 * business-hour window declared via data-* attributes.
 *
 * Status contract written to each root element:
 *
 *   data-support-status="online" | "offline"
 *   data-support-today="Mon" | "Tue" | …   (current weekday, short)
 *   data-support-hour="0" .. "23"
 *
 * Plus the copy on any descendant `[data-support-status-label]` is
 * swapped for the merchandiser-editable online / offline strings.
 *
 * Expected authoring attributes (all optional — sane defaults):
 *   data-open-hour="9"
 *   data-close-hour="16"
 *   data-open-days="Mon,Tue,Wed,Thu,Fri"
 *   data-label-online="Online now"
 *   data-label-offline="Back at 9am MST"
 *
 * Refresh cadence: once a minute + on `visibilitychange`, so the dot
 * flips the instant a backgrounded tab comes forward.
 */
(function () {
  'use strict';

  if (window._ddSupportHoursInit) return;
  window._ddSupportHoursInit = true;

  var TZ = 'America/Denver';
  var REFRESH_MS = 60 * 1000;

  function nowInDenver() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    var bag = Object.create(null);
    for (var i = 0; i < parts.length; i++) {
      bag[parts[i].type] = parts[i].value;
    }
    var hour = parseInt(bag.hour, 10);
    if (isNaN(hour)) hour = 0;
    if (hour >= 24) hour = 0;
    return {
      weekday: bag.weekday,
      hour: hour,
      minute: parseInt(bag.minute, 10) || 0,
    };
  }

  function isWithinHours(t, openHour, closeHour, openDays) {
    if (openDays.indexOf(t.weekday) === -1) return false;
    if (t.hour < openHour) return false;
    if (t.hour >= closeHour) return false;
    return true;
  }

  function parseIntAttr(el, name, fallback) {
    var raw = el.getAttribute(name);
    var n = parseInt(raw, 10);
    return isNaN(n) ? fallback : n;
  }

  function parseDaysAttr(raw) {
    if (!raw) return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    return raw
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  function apply(root) {
    var openHour = parseIntAttr(root, 'data-open-hour', 9);
    var closeHour = parseIntAttr(root, 'data-close-hour', 16);
    var openDays = parseDaysAttr(root.getAttribute('data-open-days'));
    var t = nowInDenver();
    var online = isWithinHours(t, openHour, closeHour, openDays);

    root.setAttribute('data-support-status', online ? 'online' : 'offline');
    root.setAttribute('data-support-today', t.weekday);
    root.setAttribute('data-support-hour', String(t.hour));

    var labels = root.querySelectorAll('[data-support-status-label]');
    if (labels.length) {
      var onlineLabel =
        root.getAttribute('data-label-online') || 'Online now';
      var offlineLabel =
        root.getAttribute('data-label-offline') || 'Back at 9am MST';
      var copy = online ? onlineLabel : offlineLabel;
      for (var i = 0; i < labels.length; i++) labels[i].textContent = copy;
    }
  }

  function refreshAll() {
    var nodes = document.querySelectorAll('[data-dd-support-hours]');
    for (var i = 0; i < nodes.length; i++) apply(nodes[i]);
  }

  function start() {
    refreshAll();
    setInterval(refreshAll, REFRESH_MS);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) refreshAll();
    });
    // Section-editor reloads insert new nodes without reloading the
    // page — pick them up so the theme editor stays live-accurate.
    document.addEventListener('shopify:section:load', refreshAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
